import React from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Form,
  Button,
  Modal,
  Icon,
  Message,
  Input,
  Checkbox,
} from 'semantic-ui-react';

import { get, isEmpty, toNumber } from 'lodash';
import Validator from 'validatorjs';
import {
  utcMoment,
  dateTimeFormattedWithOutTimeZone,
} from '../../../utilities/dateUtils';
import client from '../../../apollo';
import activeDealerTypeQuery from '../../../graphql/activeDealerTypeQuery';
import ExceptionForm, { initExceptionEndTimeString, initIsNoEndDate, onFormatInput } from '../Form';

class DealerExceptionForm extends ExceptionForm {
  constructor(props) {
    super(props);
    this.state = {
      id: '',
      gracePeriod: '',
      exceptionEndTime: '',
      exceptionEndTimeTemp: '',
      dealerNumber: '',
      dealerName: '',
      createdBy: '',
      createdAt: '',
      updatedBy: '',
      updatedAt: '',
      isNoEndDate: false,
      action: 'Create',
      errors: {},
      exceptionEndTimeString: '',
      debounceTimeout: null,
    };
  }

  componentDidMount() {
    const { dealerException } = this.props;
    const {
      id,
      exceptionSourceId,
      exceptionSourceName,
      gracePeriod,
      exceptionEndTime,
      createdBy,
      createdAt,
      updatedBy,
      updatedAt,
    } = dealerException;
    this.setState({
      id: toNumber(id),
      dealerNumber: exceptionSourceId,
      dealerName: exceptionSourceName,
      gracePeriod,
      exceptionEndTime,
      exceptionEndTimeTemp: exceptionEndTime,
      exceptionEndTimeString: initExceptionEndTimeString(exceptionEndTime),
      createdBy,
      createdAt,
      updatedBy,
      updatedAt,
      isNoEndDate: initIsNoEndDate(id, exceptionEndTime),
      action: id === 0 ? 'Create' : 'Update',
    });
  }

  queryDealerNameByNumber = dealerNumber => {
    client
      .query({
        query: activeDealerTypeQuery,
        variables: {
          dealerNo: dealerNumber.toString(),
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const result = get(data, 'activeDealer');
        const dealerNameFromResult = !isEmpty(result) ? result.dealerName : '';
        const active = !isEmpty(result) ? result.active : undefined;
        if (active !== null) {
          if (!isEmpty(dealerNameFromResult)) {
            this.setState({
              dealerName: dealerNameFromResult,
              loadingDealerName: false,
            });
          }
        } else {
          // active undefined means dealer does not exist from APMS but exist from OVC
          this.setErrorByField(
            'dealerNumber',
            'Dealer Number does not exist. Please enter a valid Dealer Number.'
          );
        }
        this.setState({ loadingDealerName: false });
      })
      .catch(() => {
        // console.log('Fail to get dealerName', dealerNumber, error);
      });
  };

  onChangeHandler = field => event => {
    this.setErrorByField(field, '');
    this.setState({ [field]: event.target.value });
    if (field === 'dealerNumber') {
      const dNumber = event.target.value;

      if (this.state.debounceTimeout) {
        clearTimeout(this.state.debounceTimeout);
      }

      // Set a new timer to delay the API call by 1 second
      const debounceTimeout = setTimeout(() => {
        this.queryDealerNameByNumber(dNumber); // call graphql and set value to dealer name
      }, 1000);

      this.setState({
        loadingDealerName: true,
        debounceTimeout,
      });
    }
  };

  submitForm = () => {
    this.setState({
      error: null,
      errors: {},
    });
    const { onSubmitHandler } = this.props;
    const {
      id,
      dealerNumber,
      gracePeriod,
      exceptionEndTime,
      exceptionEndTimeString,
      action,
      errors,
    } = this.state;
    const validationRules = {
      dealerNumber: 'required|integer|min: 5000000|max: 5999999',
      gracePeriod: 'required|integer|min: 0|max: 60',
    };

    const customerErrorMessage = {
      'required.dealerNumber': 'The Dealer Number field is required.',
      'required.gracePeriod': 'The Grace Period field is required.',
      'min.gracePeriod': 'Grace Period must be greater than or equal to zero.',
      'max.gracePeriod': 'Grace Period must be less than or equal to 60.',
      'min.dealerNumber':
        'Dealer Number does not exist. Please enter a valid Dealer Number.',
      'max.dealerNumber':
        'Dealer Number does not exist. Please enter a valid Dealer Number.',
    };
    const validation = new Validator(
      this.state,
      validationRules,
      customerErrorMessage
    );

    const messErrorDate = this.validateEndDateDate(exceptionEndTimeString);
    if (!isEmpty(messErrorDate) || validation.fails()) {
      const newErrors = {
        ...validation.errors.errors,
        exceptionEndTime: messErrorDate,
      };
      this.setState({ errors: newErrors });
      return false;
    } if (!isEmpty(errors.dealerNumber)) {
      return false;
    }
    onSubmitHandler({
      id: parseInt(id, 10),
      dealerNumber: dealerNumber || '',
      gracePeriod: gracePeriod || '',
      exceptionEndTime: isEmpty(exceptionEndTime)
        ? ''
        : dateTimeFormattedWithOutTimeZone(utcMoment(exceptionEndTime)),
      action,
    });
    return true;
  };

  render() {
    const {
      isModalOpen, onCloseHandler, error, submitting,
    } = this.props;

    const {
      loadingDealerName,
      exceptionEndTime,
      dealerNumber,
      dealerName,
      gracePeriod,
      isNoEndDate,
      action,
      errors,
    } = this.state;

    const exceptionEndTimeConverted = isEmpty(exceptionEndTime)
      ? null
      : utcMoment(exceptionEndTime);
    const datePickerClassName = isNoEndDate === true ? 'input-disabled' : '';
    const disabledClassName = action === 'Update' ? 'input-disabled' : '';
    const isDisableDealerNumber = action === 'Update';

    return (
      <Modal
        open={isModalOpen}
        closeIcon
        onClose={onCloseHandler}
        closeOnEscape={false}
        closeOnRootNodeClick={false}
      >
        <Modal.Header>
          {action === 'Create' ? action : 'Edit'} Dealer Exception
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {error && (
              <Message negative>
                <p>{error}</p>
              </Message>
            )}
            <Form>
              <Form.Group widths='equal'>
                <Form.Field
                  required
                  error={!isEmpty(errors.dealerNumber)}
                  width={6}
                >
                  <label htmlFor='dealerNumber'>Dealer Number</label>
                  <Input
                    id='dealerNumber'
                    className={disabledClassName}
                    disabled={isDisableDealerNumber}
                    name='dealerNumber'
                    value={dealerNumber}
                    onChange={this.onChangeHandler('dealerNumber')}
                    onKeyDown={onFormatInput}
                    type='number'
                    pattern='\d*'
                  />
                  {errors.dealerNumber && (
                    <span style={{ color: 'red' }}>
                      {errors.dealerNumber
                      && Array.isArray(errors.dealerNumber)
                      && errors.dealerNumber.length > 1
                        ? errors.dealerNumber[errors.dealerNumber.length - 1]
                        : errors.dealerNumber}
                    </span>
                  )}
                </Form.Field>
                <Form.Field width={6}>
                  {(loadingDealerName || dealerName) && (
                    <div className='field'>
                      <label htmlFor='dealerName'>Dealer Name</label>
                      <Input
                        id='dealerName'
                        disabled
                        loading={loadingDealerName}
                        value={loadingDealerName ? '...' : dealerName}
                        className='input-disabled'
                      />
                    </div>
                  )}
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field required error={!isEmpty(errors.gracePeriod)}>
                  <label htmlFor='gracePeriod'>Grace Period</label>
                  <Input
                    id='gracePeriod'
                    name='gracePeriod'
                    value={gracePeriod}
                    onChange={this.onChangeHandler('gracePeriod')}
                    onKeyDown={onFormatInput}
                    type='number'
                    min={0}
                    max={60}
                  />
                  {errors.gracePeriod && (
                    <span style={{ color: 'red' }}>
                      {errors.gracePeriod
                      && Array.isArray(errors.gracePeriod)
                      && errors.gracePeriod.length > 1
                        ? errors.gracePeriod[errors.gracePeriod.length - 1]
                        : errors.gracePeriod}
                    </span>
                  )}
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field required error={!isEmpty(errors.exceptionEndTime)}>
                  <label htmlFor='exceptionEndTime'>Exception End Date</label>
                  <Checkbox
                    onClick={this.toggleEndDate}
                    label='No end date'
                    checked={isNoEndDate}
                  />

                  <DatePicker
                    id='exceptionEndTime'
                    className={datePickerClassName}
                    style={{ padding: '0', background: 'red' }}
                    selected={exceptionEndTimeConverted}
                    onChange={this.onDateChangeHandler('exceptionEndTime')}
                    onBlur={this.onDateFocusHandler('exceptionEndTimeString')}
                    dateFormat='YYYY-MM-DD'
                    disabled={isNoEndDate}
                    value={isNoEndDate ? '' : null}
                    minDate={this.today}
                  />
                  {errors.exceptionEndTime && (
                    <span style={{ color: 'red' }}>
                      {errors.exceptionEndTime}
                    </span>
                  )}
                </Form.Field>
              </Form.Group>
            </Form>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          {submitting && (
            <Button loading positive>
              Submitting...
            </Button>
          )}
          {!submitting && (
            <Button color='green' onClick={this.submitForm} inverted>
              <Icon name='checkmark' /> {action}
            </Button>
          )}
          <Button onClick={onCloseHandler}>Cancel</Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

DealerExceptionForm.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  error: PropTypes.string,
  submitting: PropTypes.bool,
  dealerException: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exceptionSourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exceptionSourceName: PropTypes.string,
    gracePeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exceptionEndTime: PropTypes.string,
    createdBy: PropTypes.string,
    createdAt: PropTypes.string,
    updatedBy: PropTypes.string,
    updatedAt: PropTypes.string,
  }).isRequired,
};

DealerExceptionForm.defaultProps = {
  error: null,
  submitting: false,
};

export default DealerExceptionForm;
